import type { ProductContextId } from '@vs-shop/schemas/id/ProductsContextId';
import { Context, Effect, HashMap, HashSet, Layer, Option, Stream, SubscriptionRef } from 'effect';

import type {
  WarehouseCode,
  WarehouseFilterStateSet,
} from '@vs-shop/schemas/product/filter/state/warehouse';
import { ContextTag } from '../context';

export type ContextualWarehouseFilterStateSet = HashMap.HashMap<
  ProductContextId,
  WarehouseFilterStateSet
>;

export const emptyWarehouseSet = HashSet.empty<WarehouseCode>();

export const createWarehouseFiltersStream = (
  ref: SubscriptionRef.SubscriptionRef<ContextualWarehouseFilterStateSet>,
  context: ProductContextId
) => {
  return SubscriptionRef.changes(ref).pipe(
    Stream.changes,
    Stream.map((contextMap) => HashMap.get(contextMap, context)),
    Stream.map(Option.getOrElse(() => emptyWarehouseSet)),
    Stream.changes
  );
};

export const createIsWarehouseSelectedStream = (
  ref: SubscriptionRef.SubscriptionRef<ContextualWarehouseFilterStateSet>,
  context: ProductContextId,
  warehouseCode: WarehouseCode
) =>
  SubscriptionRef.changes(ref).pipe(
    Stream.map((contextMap) => HashMap.get(contextMap, context)),
    Stream.map(
      Option.match({
        onNone: () => false,
        onSome: (warehouseSet) => HashSet.has(warehouseSet, warehouseCode),
      })
    )
  );

export const createWarehouseHasActiveFiltersStream = (
  ref: SubscriptionRef.SubscriptionRef<ContextualWarehouseFilterStateSet>,
  context: ProductContextId
) =>
  createWarehouseFiltersStream(ref, context).pipe(
    Stream.map((warehouses) => HashSet.size(warehouses) > 0)
  );

export class WarehouseFilterService extends Context.Service<WarehouseFilterService>()(
  ContextTag.WarehouseFilterService,
  {
    make: Effect.gen(function* () {
      const emptyContextMap = HashMap.empty<ProductContextId, WarehouseFilterStateSet>();

      const filtersRef =
        yield* SubscriptionRef.make<ContextualWarehouseFilterStateSet>(emptyContextMap);

      const getWarehouses = (context: ProductContextId) =>
        Effect.gen(function* () {
          const contextMap = yield* SubscriptionRef.get(filtersRef);
          return HashMap.get(contextMap, context).pipe(Option.getOrElse(() => emptyWarehouseSet));
        });

      const addWarehouse = (context: ProductContextId, warehouseCode: WarehouseCode) =>
        SubscriptionRef.update(filtersRef, (contextMap) => {
          const currentWarehouses = HashMap.get(contextMap, context).pipe(
            Option.getOrElse(() => emptyWarehouseSet)
          );
          const updatedWarehouses = HashSet.add(currentWarehouses, warehouseCode);
          return HashMap.set(contextMap, context, updatedWarehouses);
        });

      const removeWarehouse = (context: ProductContextId, warehouseCode: WarehouseCode) =>
        SubscriptionRef.update(filtersRef, (contextMap) => {
          const currentWarehouses = HashMap.get(contextMap, context).pipe(
            Option.getOrElse(() => emptyWarehouseSet)
          );
          const updatedWarehouses = HashSet.remove(currentWarehouses, warehouseCode);

          if (HashSet.size(updatedWarehouses) === 0) {
            return HashMap.remove(contextMap, context);
          }

          return HashMap.set(contextMap, context, updatedWarehouses);
        });

      const toggleWarehouse = (context: ProductContextId, warehouseCode: WarehouseCode) =>
        Effect.gen(function* () {
          const currentWarehouses = yield* getWarehouses(context);
          if (HashSet.has(currentWarehouses, warehouseCode)) {
            yield* removeWarehouse(context, warehouseCode);
          } else {
            yield* addWarehouse(context, warehouseCode);
          }
        });

      const clearWarehouses = (context: ProductContextId) =>
        SubscriptionRef.update(filtersRef, HashMap.remove(context));

      const setWarehouses = (context: ProductContextId, warehouses: WarehouseFilterStateSet) =>
        SubscriptionRef.update(filtersRef, HashMap.set(context, warehouses));

      const getWarehousesStream = (context: ProductContextId) =>
        createWarehouseFiltersStream(filtersRef, context);

      const isWarehouseSelectedStream = (context: ProductContextId, warehouseCode: WarehouseCode) =>
        createIsWarehouseSelectedStream(filtersRef, context, warehouseCode);

      const hasActiveFiltersStream = (context: ProductContextId) =>
        createWarehouseHasActiveFiltersStream(filtersRef, context);

      return {
        filtersRef,
        getWarehouses,
        addWarehouse,
        removeWarehouse,
        toggleWarehouse,
        clearWarehouses,
        setWarehouses,
        getWarehousesStream,
        isWarehouseSelectedStream,
        hasActiveFiltersStream,
      };
    }),
  }
) {
  static readonly layer = Layer.effect(this, this.make);
}
